/**
 * LinkedIn-style primary destinations for the AppShell top bar.
 * Icon above label on sm+; icon-only on xs. Flagged items hide when off.
 */
import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Box, ButtonBase, Typography } from '@mui/material';
import { topNavItems } from '../../config/navigation';
import useFeatureFlag from '../../hooks/useFeatureFlag';

const isActivePath = (pathname, path) => {
  if (path === '/') return pathname === '/';
  if (path === '/marketplace') {
    return (
      pathname === '/marketplace' ||
      (pathname.startsWith('/marketplace/') && !pathname.startsWith('/marketplace/apply'))
    );
  }
  return pathname.startsWith(path);
};

const TopNavLinks = () => {
  const location = useLocation();
  const { enabled: communityEnabled } = useFeatureFlag('community');
  const { enabled: marketplaceEnabled } = useFeatureFlag('interviewer_marketplace');

  const visibleItems = topNavItems.filter((item) => {
    if (item.featureFlag === 'community' && !communityEnabled) return false;
    if (item.featureFlag === 'interviewer_marketplace' && !marketplaceEnabled) return false;
    return true;
  });

  return (
    <Box
      component="nav"
      aria-label="Primary"
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: { xs: 'flex-start', md: 'center' },
        flex: '1 1 0%',
        gap: { xs: 0.25, sm: 0.5, md: 1 },
        minWidth: 0,
        overflowX: 'auto',
        overflowY: 'hidden',
        flexWrap: 'nowrap',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      {visibleItems.map((item) => {
        const Icon = item.icon;
        const active = isActivePath(location.pathname, item.path);
        return (
          <ButtonBase
            key={item.path}
            component={RouterLink}
            to={item.path}
            aria-label={item.label}
            aria-current={active ? 'page' : undefined}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              px: { xs: 0.5, sm: 1, md: 1.5 },
              py: 0.75,
              minWidth: { xs: 36, sm: 48, md: 64 },
              flex: '0 0 auto',
              color: active ? 'text.primary' : 'text.secondary',
              borderBottom: '2px solid',
              borderColor: active ? 'text.primary' : 'transparent',
              borderRadius: 0,
              transition: 'color 0.15s ease, border-color 0.15s ease',
              '&:hover': {
                color: 'text.primary',
                bgcolor: 'action.hover',
              },
            }}
          >
            <Icon
              sx={{
                fontSize: { xs: 22, md: 24 },
                mb: { xs: 0, sm: 0.25 },
              }}
            />
            <Typography
              component="span"
              variant="caption"
              sx={{
                display: { xs: 'none', sm: 'block' },
                fontSize: '0.7rem',
                fontWeight: active ? 600 : 500,
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
              }}
            >
              {item.label}
            </Typography>
          </ButtonBase>
        );
      })}
    </Box>
  );
};

export default TopNavLinks;
export { isActivePath };
